import { useRef } from 'react';
import { Loader2, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';

interface TimesheetDateRangeSelectorProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  startDatePickerOpen: boolean;
  endDatePickerOpen: boolean;
  setStartDatePickerOpen: (open: boolean) => void;
  setEndDatePickerOpen: (open: boolean) => void;
}

export function TimesheetDateRangeSelector({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onGenerate,
  isGenerating,
  startDatePickerOpen,
  endDatePickerOpen,
  setStartDatePickerOpen,
  setEndDatePickerOpen,
}: TimesheetDateRangeSelectorProps) {
  const generateButtonRef = useRef<HTMLButtonElement>(null);

  const handleStartDateChange = (date: Date | undefined) => {
    onStartDateChange(date);
    if (date) {
      setStartDatePickerOpen(false);
      setEndDatePickerOpen(true);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    onEndDateChange(date);
    if (date) {
      setEndDatePickerOpen(false);
      setTimeout(() => {
        generateButtonRef.current?.focus();
      }, 100);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h2 className="text-lg font-semibold mb-4">Generate Report</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            placeholder="Select start date"
            open={startDatePickerOpen}
            onOpenChange={setStartDatePickerOpen}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            placeholder="Select end date"
            open={endDatePickerOpen}
            onOpenChange={setEndDatePickerOpen}
          />
        </div>

        <div className="flex items-end">
          <Button
            ref={generateButtonRef}
            onClick={onGenerate}
            disabled={isGenerating || !startDate || !endDate}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <CalendarIcon className="mr-2 h-4 w-4" />
                Generate Report
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
